import { useState, useCallback, useRef, useEffect } from 'react';

export function useVolumeNormalization() {
  const [normalizationData, setNormalizationData] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [normalizationApplied, setNormalizationApplied] = useState(0);
  const [showNormalizationIndicator, setShowNormalizationIndicator] = useState(false);
  const analysisCache = useRef(new Map());

  // Simple auto-hide with useEffect
  useEffect(() => {
    if (showNormalizationIndicator) {
      console.log('🔊 Volume Normalization: Indicator shown, setting 10-second timer');
      const timer = setTimeout(() => {
        console.log('🔊 Volume Normalization: 10 seconds elapsed, hiding indicator');
        setShowNormalizationIndicator(false);
      }, 10000);

      return () => {
        console.log('🔊 Volume Normalization: Cleaning up timer');
        clearTimeout(timer);
      };
    }
  }, [showNormalizationIndicator]);

  // Fast audio peak analysis using Web Audio API with optimized sampling
  const analyzeTrackPeaks = useCallback(async (file) => {
    return new Promise((resolve) => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const fileReader = new FileReader();
      
      fileReader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Get audio data from all channels
          const channelData = [];
          for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            channelData.push(audioBuffer.getChannelData(channel));
          }
          
          // Fast sampling - analyze every 1000th sample for speed
          const sampleRate = Math.max(1000, Math.floor(audioBuffer.length / 5000)); // Max 5000 samples
          let maxPeak = 0;
          let rmsSum = 0;
          let sampleCount = 0;
          
          for (let i = 0; i < audioBuffer.length; i += sampleRate) {
            let sampleSquareSum = 0;
            let sampleMax = 0;
            
            // Average across all channels for this sample
            for (let channel = 0; channel < channelData.length; channel++) {
              const sample = Math.abs(channelData[channel][i] || 0);
              sampleMax = Math.max(sampleMax, sample);
              sampleSquareSum += sample * sample;
            }
            
            maxPeak = Math.max(maxPeak, sampleMax);
            rmsSum += sampleSquareSum / channelData.length;
            sampleCount++;
          }
          
          const rms = Math.sqrt(rmsSum / sampleCount);
          const avgPeak = rms * 1.4; // Estimate average peak from RMS
          
          console.log(`🔊 Fast analysis completed - Peak: ${maxPeak.toFixed(3)}, RMS: ${rms.toFixed(3)}, Duration: ${audioBuffer.duration.toFixed(1)}s`);
          
          resolve({
            maxPeak: maxPeak,
            avgPeak: avgPeak,
            rms: rms,
            duration: audioBuffer.duration
          });
          
          audioContext.close();
          
        } catch (error) {
          console.warn('Fast peak analysis failed:', error);
          audioContext.close();
          resolve({
            maxPeak: 0.7,
            avgPeak: 0.5,
            rms: 0.4,
            duration: 180 // Default 3 minutes
          });
        }
      };
      
      fileReader.onerror = () => {
        audioContext.close();
        resolve({
          maxPeak: 0.7,
          avgPeak: 0.5,
          rms: 0.4,
          duration: 180
        });
      };
      
      fileReader.readAsArrayBuffer(file);
    });
  }, []);

  // Parallel analysis of all tracks for maximum speed
  const analyzePlaylist = useCallback(async (playlist) => {
    console.log('🔊 Volume Normalization: Starting FAST playlist analysis', playlist);
    
    if (!playlist || playlist.length === 0) {
      console.log('🔊 Volume Normalization: Empty playlist, skipping analysis');
      return {};
    }

    setIsAnalyzing(true);
    const startTime = Date.now();
    
    try {
      // Create analysis promises for all tracks simultaneously
      const analysisPromises = playlist.map(async (song, index) => {
        console.log(`🔊 Volume Normalization: Starting analysis ${index + 1}/${playlist.length}: ${song.title || song.name}`);
        
        if (!song.file) {
          return {
            songId: song.id,
            analysis: {
              maxPeak: 0.7,
              avgPeak: 0.5,
              rms: 0.4,
              duration: song.duration || 180,
              gainAdjustment: 1.0
            }
          };
        }

        // Check cache first
        const cacheKey = `${song.file.name}-${song.file.size}-${song.file.lastModified}`;
        if (analysisCache.current.has(cacheKey)) {
          console.log(`🔊 Volume Normalization: Using cached analysis for ${song.title || song.name}`);
          return {
            songId: song.id,
            analysis: analysisCache.current.get(cacheKey)
          };
        }

        try {
          const analysis = await analyzeTrackPeaks(song.file);
          const result = {
            ...analysis,
            gainAdjustment: 1.0
          };
          
          // Cache the result
          analysisCache.current.set(cacheKey, result);
          console.log(`🔊 Volume Normalization: Completed fast analysis for ${song.title || song.name}`, analysis);
          
          return {
            songId: song.id,
            analysis: result
          };
        } catch (error) {
          console.warn(`🔊 Volume Normalization: Analysis failed for ${song.name}:`, error);
          return {
            songId: song.id,
            analysis: {
              maxPeak: 0.7,
              avgPeak: 0.5,
              rms: 0.4,
              duration: song.duration || 180,
              gainAdjustment: 1.0
            }
          };
        }
      });

      // Wait for all analyses to complete in parallel
      console.log('🔊 Volume Normalization: Running parallel analysis...');
      const analysisResults = await Promise.all(analysisPromises);
      
      // Convert to results object
      const results = {};
      analysisResults.forEach(({ songId, analysis }) => {
        results[songId] = analysis;
      });

      const analysisTime = Date.now() - startTime;
      console.log(`🔊 Volume Normalization: Parallel analysis completed in ${analysisTime}ms`);

      // Calculate normalization gains with improved algorithm
      const normalizedResults = calculateNormalizationGains(results);
      setNormalizationData(normalizedResults);
      
      // Calculate and show normalization applied indicator
      const normalizationStats = calculateNormalizationStats(normalizedResults);
      const normalizationPercentage = normalizationStats.averageAdjustmentPercent;
      
      console.log('🔊 Volume Normalization: Calculated normalization percentage:', normalizationPercentage);
      console.log('🔊 Volume Normalization: Stats:', normalizationStats);
      
      setNormalizationApplied(normalizationPercentage);
      
      if (normalizationPercentage > 3) {
        console.log('🔊 Volume Normalization: Showing indicator for', normalizationPercentage, '%');
        setShowNormalizationIndicator(true);
      } else {
        console.log('🔊 Volume Normalization: Normalization below threshold, not showing indicator');
        setShowNormalizationIndicator(false);
      }

      return normalizedResults;
      
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  // Improved normalization calculation with better detection
  const calculateNormalizationGains = useCallback((analysisResults) => {
    const tracks = Object.values(analysisResults);
    if (tracks.length === 0) return analysisResults;

    console.log('🔊 Volume Normalization: Calculating gains for', tracks.length, 'tracks');

    // Extract valid audio levels
    const rmsValues = tracks.map(t => t.rms).filter(r => r > 0.01); // Filter out very quiet tracks
    const maxPeaks = tracks.map(t => t.maxPeak).filter(p => p > 0.01);

    if (rmsValues.length === 0) {
      console.log('🔊 Volume Normalization: No valid audio levels found');
      return analysisResults;
    }

    // Calculate target levels using more sensitive approach
    const sortedRMS = [...rmsValues].sort((a, b) => a - b);
    const medianRMS = sortedRMS[Math.floor(sortedRMS.length / 2)];
    const avgRMS = rmsValues.reduce((sum, val) => sum + val, 0) / rmsValues.length;
    
    // Use average of median and mean for more balanced targeting
    const targetRMS = (medianRMS + avgRMS) / 2;
    const maxAllowedPeak = 0.95; // Prevent clipping
    
    console.log('🔊 Volume Normalization: Target RMS:', targetRMS.toFixed(3), 'Median:', medianRMS.toFixed(3), 'Average:', avgRMS.toFixed(3));

    // Apply normalization to each track
    const normalizedResults = {};
    
    Object.keys(analysisResults).forEach(songId => {
      const analysis = analysisResults[songId];
      let gainAdjustment = 1.0;
      
      if (analysis.rms > 0.01) { // Only normalize tracks with meaningful audio
        // Calculate gain based on RMS difference from target
        gainAdjustment = targetRMS / analysis.rms;
        
        // Limit gain to prevent clipping
        const peakAfterGain = analysis.maxPeak * gainAdjustment;
        if (peakAfterGain > maxAllowedPeak) {
          gainAdjustment = maxAllowedPeak / analysis.maxPeak;
        }
        
        // More sensitive range (0.5x to 2.5x instead of 0.3x to 3.0x)
        gainAdjustment = Math.max(0.5, Math.min(2.5, gainAdjustment));
        
        console.log(`🔊 Track ${songId}: RMS ${analysis.rms.toFixed(3)} -> Gain ${gainAdjustment.toFixed(3)}x`);
      }
      
      normalizedResults[songId] = {
        ...analysis,
        gainAdjustment: gainAdjustment
      };
    });

    return normalizedResults;
  }, []);

  // Calculate normalization statistics for better reporting
  const calculateNormalizationStats = useCallback((normalizedResults) => {
    const tracks = Object.values(normalizedResults);
    const gainAdjustments = tracks.map(t => t.gainAdjustment);
    
    // Calculate how much normalization was applied
    const adjustmentDeviations = gainAdjustments.map(gain => Math.abs(1.0 - gain));
    const maxDeviation = Math.max(...adjustmentDeviations);
    const avgDeviation = adjustmentDeviations.reduce((sum, dev) => sum + dev, 0) / adjustmentDeviations.length;
    
    // Calculate percentage based on both average and maximum deviations
    const averageAdjustmentPercent = Math.round(avgDeviation * 100);
    const maxAdjustmentPercent = Math.round(maxDeviation * 100);
    
    // Use the higher of the two for reporting
    const reportedPercent = Math.max(averageAdjustmentPercent, maxAdjustmentPercent);
    
    return {
      averageAdjustmentPercent: reportedPercent,
      maxAdjustmentPercent,
      tracksAdjusted: adjustmentDeviations.filter(dev => dev > 0.05).length, // Tracks with >5% adjustment
      totalTracks: tracks.length
    };
  }, []);

  // Apply normalization to audio element
  const applyNormalization = useCallback((audioElement, songId) => {
    if (!audioElement || !normalizationData[songId]) return;

    const gainData = normalizationData[songId];
    const gainNode = audioElement.gainNode;
    
    if (gainNode && gainData.gainAdjustment !== 1.0) {
      // Smooth gain transition to prevent audio pops
      const currentTime = gainNode.context.currentTime;
      gainNode.gain.cancelScheduledValues(currentTime);
      gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
      gainNode.gain.linearRampToValueAtTime(gainData.gainAdjustment, currentTime + 0.1);
      
      console.log(`🔊 Applied normalization: ${gainData.gainAdjustment.toFixed(3)}x gain for song ${songId}`);
    }
  }, [normalizationData]);

  // Get normalization info for a specific song
  const getNormalizationInfo = useCallback((songId) => {
    return normalizationData[songId] || null;
  }, [normalizationData]);

  // Clear normalization data
  const clearNormalization = useCallback(() => {
    setNormalizationData({});
    setNormalizationApplied(0);
    setShowNormalizationIndicator(false);
    analysisCache.current.clear();
  }, []);

  return {
    normalizationData,
    isAnalyzing,
    normalizationApplied,
    showNormalizationIndicator,
    analyzePlaylist,
    applyNormalization,
    getNormalizationInfo,
    clearNormalization
  };
}